import { union } from 'lodash-es';
import type { RoleItem } from './types';
import { getRoles } from '../../../apis/methods/role';
import forEachDeep from '../../../utils/function/forEachDeep';
import type { MenuBaseOn } from '../settings/types';
import { getMenuTreeAdapter } from '../menu/adapter';
import { useRouter } from 'vue-router';

/**
 * 多角色虚拟code
 */
export const ROLE_MULTI_CODE = 'role_multi_code';

/**
 * 多角色虚拟item
 */
export const getMultiRoleItem = (): RoleItem => {
  return {
    roleId: ROLE_MULTI_CODE,
    roleCode: ROLE_MULTI_CODE, // 角色编码
    roleName: '多角色',
    isDefaultRole: '0',
    permissions: [],
    position: '',
  };
};

/**
 * 获取角色数据
 */
export const getRolesData = async (baseOn: MenuBaseOn, subSystemCode: string): Promise<RoleItem[]> => {
  let roles: RoleItem[] = [];
  try {
    const res = await getRoles(baseOn);
    if (!res?.success) {
      return roles;
    }

    // 只需要配置的baspSubSystemCode子系统的角色和权限
    roles = (res.obj as RoleItem[])
      .filter((item) => item.subsysCode === subSystemCode)
      .map((item) => {
        return {
          roleId: item.roleId,
          roleName: item.roleName,
          roleCode: item.roleCode,
          isDefaultRole: item.isDefaultRole,
          permissions: [] as string[], // 先初始化为空数组，后面再赋值
          position: item.position,
        };
      });
  } catch (e) {
    console.error(e);
  }
  return roles;
};

/**
 * 获取角色的权限数据
 */
export const getPermissionData = async (baseOn: MenuBaseOn, roleItem: RoleItem): Promise<string[]> => {
  const getMenuTreeFunc = getMenuTreeAdapter(baseOn);
  const router = useRouter();
  const { routes = [] } = router?.options || {};
  const res = await getMenuTreeFunc?.(baseOn, routes, roleItem.roleId);
  const permissions: string[] = [];
  // todo: 这一块逻辑需要仔细梳理下
  if (res?.length) {
    // 合并打平权限结构
    forEachDeep(
      res,
      ({ moduleCode, hidden }) => {
        if (hidden === 'Y') {
          return true;
        }
        if (moduleCode && !permissions.includes(moduleCode)) {
          permissions.push(moduleCode);
        }
      },
      'childModules',
    );
  }
  return permissions;
};

/**
 * 获取角色权限数据
 */
export const getRolePermissionData = async (baseOn: MenuBaseOn, subSystemCode: string): Promise<RoleItem[]> => {
  const roleData = await getRolesData(baseOn, subSystemCode);
  return roleData;

  // 下面这一块后续再慢慢梳理，核心原则是获取用户角色 -> 根据角色获取菜单
  if (roleData.length === 0) {
    return roleData;
  }

  // 根据角色获取百源权限
  // todo: 多角色控制
  await Promise.all(
    roleData
      .filter((roleItem) => roleItem.roleCode !== ROLE_MULTI_CODE)
      .map(async (roleItem) => {
        roleItem.permissions = await getPermissionData(baseOn, roleItem);
      }),
  );

  if (roleData.length > 1) {
    // 多个角色时添加多角色数据
    const permissionsDataMulti = getMultiRoleItem();
    // 多角色包含所有此用户所有权限
    permissionsDataMulti.permissions = union(...Object.values(roleData.map((item) => item.permissions)));
    roleData.unshift(permissionsDataMulti);
  }

  return roleData;
};
